/**
 * Poseidon dashboard — operator-facing labels and tones.
 *
 * The daemon sends stable machine identifiers; every human-readable string is
 * resolved here so wording stays consistent across pages and so a wording change
 * never requires an API change. Where the spec fixes the wording (§3.2 A ribbon
 * fields, §3.2 E "research adjustment"), the spec's words are used verbatim.
 */
import type {
  BacktestKind,
  BacktestStatus,
  ConvictionTier,
  CoverageStatus,
  DeepSeekHealth,
  EventDirection,
  ExecutionMode,
  ExitReason,
  HaltLevel,
  KpiId,
  Mt5ConnectionState,
  ResearchRecommendation,
  ResearchStatus,
  RiskLimitId,
  RiskState,
  RiskVerdict,
  SchemaValidationStatus,
  SessionPhase,
  SessionPhaseBucket,
  Severity,
  SystemEventKind,
  Timeframe,
  TradeOutcome,
  TrendBucket,
  VolBucket,
  WorkingOrderState,
} from '../api/types';
import type { Tone } from './staleness';

export const MT5_LABELS: Record<Mt5ConnectionState, string> = {
  connected: 'connected',
  disconnected: 'disconnected',
};

export const EXECUTION_MODE_LABELS: Record<ExecutionMode, string> = {
  disabled: 'disabled',
  approval_required: 'approval-required',
  demo_enabled: 'demo-enabled',
};

export const SESSION_PHASE_LABELS: Record<SessionPhase, string> = {
  pre_market: 'pre-market',
  active: 'active',
  no_new_trades: 'no-new-trades',
  flattening: 'flattening',
  closed: 'closed',
};

export const RISK_STATE_LABELS: Record<RiskState, string> = {
  normal: 'normal',
  warning: 'warning',
  soft_halted: 'soft-halted',
  hard_halted: 'hard-halted',
};

export const DEEPSEEK_LABELS: Record<DeepSeekHealth, string> = {
  healthy: 'healthy',
  degraded: 'degraded',
  unavailable: 'unavailable',
};

export const HALT_LABELS: Record<HaltLevel, string> = {
  none: 'no halt',
  soft: 'SOFT HALT',
  hard: 'HARD HALT',
};

export const WORKING_ORDER_STATE_LABELS: Record<WorkingOrderState, string> = {
  pending_new: 'pending new',
  placed: 'placed',
  partially_filled: 'partially filled',
  pending_cancel: 'pending cancel',
  pending_modify: 'pending modify',
  rejected: 'rejected',
  expired: 'expired',
  unknown: 'unknown',
};

export const OUTCOME_LABELS: Record<TradeOutcome, string> = {
  win: 'win',
  loss: 'loss',
  breakeven: 'breakeven',
};

export const EXIT_REASON_LABELS: Record<ExitReason, string> = {
  stop: 'stop',
  target: 'target',
  time: 'time',
  signal: 'signal',
  risk: 'risk',
  manual: 'manual',
  session_flatten: 'session flatten',
};

export const VERDICT_LABELS: Record<RiskVerdict, string> = {
  approve: 'APPROVE',
  reject: 'REJECT',
  reduce: 'REDUCE',
  defer: 'DEFER',
};

export const SEVERITY_LABELS: Record<Severity, string> = {
  debug: 'debug',
  info: 'info',
  warning: 'warning',
  error: 'error',
  critical: 'critical',
};

export const SYSTEM_EVENT_KIND_LABELS: Record<SystemEventKind, string> = {
  mt5_connection: 'MT5 connection',
  data_quality_gate: 'data-quality gate',
  risk_event: 'risk event',
  execution: 'execution',
  api_error: 'API error',
  manual_action: 'manual action',
  reconciliation: 'reconciliation',
  scheduler: 'scheduler',
  research: 'research',
  config_change: 'configuration change',
};

export const RESEARCH_STATUS_LABELS: Record<ResearchStatus, string> = {
  not_requested: 'not requested',
  pending: 'pending',
  valid: 'valid',
  stale: 'stale',
  invalid: 'invalid',
  unavailable: 'unavailable',
};

export const SCHEMA_VALIDATION_LABELS: Record<SchemaValidationStatus, string> = {
  valid: 'schema valid',
  invalid: 'schema invalid',
  rejected_unknown_field: 'rejected: unknown field',
  missing: 'no assessment',
  pending: 'pending',
};

export const RECOMMENDATION_LABELS: Record<ResearchRecommendation, string> = {
  allow_quant_signal: 'allow quantitative signal',
  allow_reduced_size: 'allow with reduced size',
  veto_quant_signal: 'veto quantitative signal',
};

export const CONVICTION_LABELS: Record<ConvictionTier, string> = {
  low: 'low',
  medium: 'medium',
  high: 'high',
};

export const COVERAGE_LABELS: Record<CoverageStatus, string> = {
  sufficient: 'sufficient',
  partial: 'partial',
  insufficient: 'insufficient',
  stale: 'stale',
  unavailable: 'unavailable',
};

export const EVENT_DIRECTION_LABELS: Record<EventDirection, string> = {
  bullish: 'bullish',
  bearish: 'bearish',
  neutral: 'neutral',
  mixed: 'mixed',
};

export const TREND_LABELS: Record<TrendBucket, string> = {
  trending: 'trending',
  ranging: 'ranging',
  unknown: 'unknown',
};

export const VOL_LABELS: Record<VolBucket, string> = {
  low: 'low vol',
  normal: 'normal vol',
  high: 'high vol',
  unknown: 'unknown vol',
};

export const SESSION_BUCKET_LABELS: Record<SessionPhaseBucket, string> = {
  opening: 'opening',
  mid_session: 'mid-session',
  pre_close: 'pre-close',
  unknown: 'unknown',
};

export const TIMEFRAME_LABELS: Record<Timeframe, string> = {
  M1: 'M1',
  M5: 'M5',
  M15: 'M15',
};

export const BACKTEST_KIND_LABELS: Record<BacktestKind, string> = {
  historical_backtest: 'historical backtest',
  tick_replay: 'tick replay',
  walk_forward: 'walk-forward',
  paper_forward: 'paper forward test',
  demo_forward: 'demo forward test',
};

export const BACKTEST_STATUS_LABELS: Record<BacktestStatus, string> = {
  queued: 'queued',
  running: 'running',
  completed: 'completed',
  failed: 'failed',
  cancelled: 'cancelled',
};

/* ------------------------------------------------------------------ *
 * KPI cards — §3.2 A order
 * ------------------------------------------------------------------ */

/** The spec's order, used to lay out the grid even if the daemon reorders cards. */
export const KPI_ORDER: readonly KpiId[] = [
  'closed_trades_today',
  'open_positions',
  'win_rate',
  'wins_losses_breakeven',
  'realized_pnl',
  'unrealized_pnl',
  'net_pnl_today',
  'gross_profit',
  'gross_loss',
  'profit_factor',
  'expectancy_per_trade',
  'average_win',
  'average_loss',
  'largest_win',
  'largest_loss',
  'current_drawdown',
  'daily_loss_remaining',
  'orders_submitted',
  'orders_rejected',
  'average_fill_latency',
  'slippage',
] as const;

export const KPI_LABELS: Record<KpiId, string> = {
  closed_trades_today: 'Closed trades today',
  open_positions: 'Open positions',
  win_rate: 'Win rate',
  wins_losses_breakeven: 'Wins / losses / breakeven',
  realized_pnl: 'Realized P&L',
  unrealized_pnl: 'Unrealized P&L',
  net_pnl_today: 'Net P&L today',
  gross_profit: 'Gross profit',
  gross_loss: 'Gross loss',
  profit_factor: 'Profit factor',
  expectancy_per_trade: 'Expectancy per trade',
  average_win: 'Average win',
  average_loss: 'Average loss',
  largest_win: 'Largest win',
  largest_loss: 'Largest loss',
  current_drawdown: 'Current drawdown',
  daily_loss_remaining: 'Daily loss remaining',
  orders_submitted: 'Orders submitted',
  orders_rejected: 'Orders rejected',
  average_fill_latency: 'Average fill latency',
  slippage: 'Estimated / actual slippage',
};

/** Short explanations, surfaced as tooltips so the numbers stay interpretable. */
export const KPI_HELP: Record<KpiId, string> = {
  closed_trades_today: 'Completed round trips today. A completed trade is a full lifecycle from first entry fill to final exit fill (§3.3).',
  open_positions: 'Number of open positions and their total notional.',
  win_rate: 'Winning closed trades divided by completed closed trades, after costs. Breakeven trades are excluded from the denominator and shown separately (§3.3).',
  wins_losses_breakeven: 'Outcome split after commission, swap and fees, using the configured breakeven threshold.',
  realized_pnl: 'Sum of net realized P&L for completed trades today.',
  unrealized_pnl: 'Mark-to-market P&L of currently open positions.',
  net_pnl_today: 'Realized + unrealized − costs.',
  gross_profit: 'Sum of winning trades before the loss side of the profit factor.',
  gross_loss: 'Sum of losing trades, shown as a negative value.',
  profit_factor: 'Gross profit divided by the absolute value of gross loss.',
  expectancy_per_trade: 'Average net P&L per completed trade.',
  average_win: 'Mean net P&L of winning trades, after costs.',
  average_loss: 'Mean net P&L of losing trades, after costs.',
  largest_win: 'Largest single completed-trade net P&L.',
  largest_loss: 'Largest single completed-trade net loss.',
  current_drawdown: 'Distance from the intraday high-water equity mark.',
  daily_loss_remaining: 'Configured daily-loss cap minus the loss taken so far. Reaching zero triggers a soft halt.',
  orders_submitted: 'Order intents the daemon submitted to the EA.',
  orders_rejected: 'Submissions rejected by risk, the EA, or the broker. A rising count is an execution-health signal.',
  average_fill_latency: 'Intent time to confirmed fill.',
  slippage: 'Actual slippage benchmarked against the decision quote, next to the estimate.',
};

/* ------------------------------------------------------------------ *
 * Risk limits — §3.2 D order
 * ------------------------------------------------------------------ */

export const RISK_LIMIT_ORDER: readonly RiskLimitId[] = [
  'daily_loss_limit',
  'per_trade_risk_limit',
  'max_open_positions',
  'max_gross_notional',
  'per_symbol_exposure_cap',
  'max_spread',
  'max_slippage',
  'max_consecutive_losses',
  'flatten_deadline',
  'halt_state',
] as const;

export const RISK_LIMIT_LABELS: Record<RiskLimitId, string> = {
  daily_loss_limit: 'Daily loss limit',
  per_trade_risk_limit: 'Per-trade risk limit',
  max_open_positions: 'Maximum open positions',
  max_gross_notional: 'Maximum gross notional / exposure',
  per_symbol_exposure_cap: 'Per-symbol exposure cap',
  max_spread: 'Maximum spread allowed',
  max_slippage: 'Maximum slippage allowed',
  max_consecutive_losses: 'Maximum consecutive losses',
  flatten_deadline: 'End-of-session flatten deadline',
  halt_state: 'Manual / automatic halt state',
};

/* ------------------------------------------------------------------ *
 * Tone helpers
 * ------------------------------------------------------------------ */

export function toneForMt5(state: Mt5ConnectionState): Tone {
  return state === 'connected' ? 'ok' : 'danger';
}

export function toneForExecutionMode(mode: ExecutionMode): Tone {
  switch (mode) {
    case 'demo_enabled':
      return 'warn';
    case 'approval_required':
      return 'info';
    case 'disabled':
      return 'muted';
  }
}

export function toneForSessionPhase(phase: SessionPhase): Tone {
  switch (phase) {
    case 'active':
      return 'ok';
    case 'pre_market':
      return 'info';
    case 'no_new_trades':
      return 'warn';
    case 'flattening':
      return 'warn';
    case 'closed':
      return 'muted';
  }
}

export function toneForRiskState(state: RiskState): Tone {
  switch (state) {
    case 'normal':
      return 'ok';
    case 'warning':
      return 'warn';
    case 'soft_halted':
      return 'danger';
    case 'hard_halted':
      return 'danger';
  }
}

export function toneForDeepSeek(health: DeepSeekHealth): Tone {
  switch (health) {
    case 'healthy':
      return 'ok';
    case 'degraded':
      return 'warn';
    case 'unavailable':
      return 'muted';
  }
}

export function toneForSeverity(severity: Severity): Tone {
  switch (severity) {
    case 'debug':
      return 'muted';
    case 'info':
      return 'info';
    case 'warning':
      return 'warn';
    case 'error':
      return 'danger';
    case 'critical':
      return 'danger';
  }
}

export function toneForVerdict(verdict: RiskVerdict): Tone {
  switch (verdict) {
    case 'approve':
      return 'ok';
    case 'reduce':
      return 'warn';
    case 'defer':
      return 'warn';
    case 'reject':
      return 'danger';
  }
}

export function toneForOutcome(outcome: TradeOutcome): Tone {
  switch (outcome) {
    case 'win':
      return 'ok';
    case 'loss':
      return 'danger';
    case 'breakeven':
      return 'muted';
  }
}

export function toneForResearchStatus(status: ResearchStatus): Tone {
  switch (status) {
    case 'valid':
      return 'ok';
    case 'pending':
      return 'info';
    case 'stale':
      return 'warn';
    case 'invalid':
      return 'danger';
    case 'unavailable':
      return 'muted';
    case 'not_requested':
      return 'muted';
  }
}

export function toneForValidation(status: SchemaValidationStatus): Tone {
  switch (status) {
    case 'valid':
      return 'ok';
    case 'pending':
      return 'info';
    case 'invalid':
      return 'danger';
    case 'rejected_unknown_field':
      return 'danger';
    case 'missing':
      return 'muted';
  }
}

export function toneForUtilization(status: 'ok' | 'warning' | 'breach' | 'not_evaluated'): Tone {
  switch (status) {
    case 'ok':
      return 'ok';
    case 'warning':
      return 'warn';
    case 'breach':
      return 'danger';
    case 'not_evaluated':
      return 'muted';
  }
}

export function toneForBacktestStatus(status: BacktestStatus): Tone {
  switch (status) {
    case 'completed':
      return 'ok';
    case 'running':
      return 'info';
    case 'queued':
      return 'muted';
    case 'failed':
      return 'danger';
    case 'cancelled':
      return 'muted';
  }
}

export function toneForReadiness(overall: 'ready' | 'degraded' | 'blocked'): Tone {
  switch (overall) {
    case 'ready':
      return 'ok';
    case 'degraded':
      return 'warn';
    case 'blocked':
      return 'danger';
  }
}

/**
 * §3.2 E: AI output must never be presented as a price oracle. This exact
 * wording is mandated by the spec and is used wherever research output appears.
 */
export const AI_OUTPUT_LABEL = 'research adjustment';

export const AI_DISCLAIMER =
  'DeepSeek output is a bounded research adjustment to a deterministic quantitative signal. It is not a price prediction, not a probability of profit, and it cannot create a trade or override the risk engine (§1.4, §6.5).';
